import { useState, useEffect, SyntheticEvent } from "react";
import axios from "axios";
import TimelineOutlined from "@mui/icons-material/TimelineOutlined";
import { DateField, StatCard, type DataTableColumn } from "@routes-plan/ui";
import ClientHistoryModal from "./modals/ClientHistoryModal";
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import { useT } from '../../i18n';
import { setPageTitle } from '../../utils/pageTitle';
import { companyLocale, formatCurrency as companyMoney } from '../../utils/format/currency';
import {
  OfficeSelect,
  ReportEmpty,
  ReportFilters,
  ReportPage,
  ReportResults,
  ReportStats,
  ReportTable,
  RouteMultiSelect,
  RowAction,
} from "./components/ReportLayout";

interface CancellationRow {
  id: number;
  route: string;
  routeId: number;
  idNumber: string;
  clientName: string;
  value: number;
  paidValue: number;
  statusDate: string;
  statusDateRaw: string;
  date: string;
  dateRaw: string;
  fullClient: Client;
}

// Interfaces mejoradas
interface Office {
  id: number;
  name: string;
}

interface Route {
  id: number;
  name: string;
}

interface Client {
  id: number;
  idNumber: string;
  fullName: string;
}

interface Loan {
  id: number;
  date: string;
  statusDate: string;
  value: number;
  totalValue: number;
  paidValue: number;
  client: Client;
  route?: {
    id: number;
    name: string;
  };
}

interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;
  selectedRouteIds: number[];
}

interface InformeCancelacionProps {
  showAlert: (
    message: string,
    severity?: "error" | "info" | "success" | "warning"
  ) => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Funciones utilitarias
const formatCurrency = (value: number): string => {
  return companyMoney(value);
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString(companyLocale());
};

// Componente principal con lógica mejorada
const Cancellations: React.FC<InformeCancelacionProps> = ({
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const t = useT();
  setPageTitle('menu.reports.cancellations');
  const currentDate = nowOperationalDateString();
  const [datas, setDatas] = useState<any[]>([]);

  // Estados
  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    selectedRouteIds: [],
  });
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [rows, setRows] = useState<CancellationRow[]>([]);
  const [queried, setQueried] = useState(false);
  const [summary, setSummary] = useState({
    totalPayments: 0,
    totalLoans: 0,
    count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);

  // Efecto para cargar oficinas al montar el componente
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await axios.get("/offices");
        if (response.data && response.data.data) {
          setOffices(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching offices:", error);
        showAlert(t('loanReports.officesLoadError'), "error");
      }
    };

    fetchOffices();
  }, [showAlert, t]);

  // Efecto para cargar cobros cuando cambia la oficina
  useEffect(() => {
    const fetchRoutes = async () => {
      if (!filter.officeId) return;

      setLoading(true);
      try {
        const response = await axios.get(`/offices/${filter.officeId}/routes`);
        if (response.data) {
          setOfficeRoutes(response.data);
          // Al cambiar la oficina se limpian las rutas elegidas
          setFilter((prev) => ({ ...prev, selectedRouteIds: [] }));
        }
      } catch (error) {
        console.error("Error fetching routes:", error);
        showAlert(t('loanReports.routesLoadError'), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchRoutes();
  }, [filter.officeId, showAlert, t]);

  const validateForm = (): boolean => {
    if (filter.selectedRouteIds.length === 0) {
      showAlert(t('loanReports.selectAtLeastOneRoute'), "warning");
      return false;
    }

    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert(t('loanReports.invalidDateRange'), "warning");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    showLoading();
    setLoading(true);
    setQueried(true);

    try {
      const response = await axios.post("/reports/cancellations", {
        startDate: filter.startDate,
        endDate: filter.endDate,
        routeId: filter.selectedRouteIds,
      });
      setDatas(response.data.data);
      const loans: Loan[] = response.data.data;
      if (!loans || loans.length === 0) {
        showAlert(t('loanReports.cancellations.empty'), "info");
        setRows([]);
        setSummary({
          totalPayments: 0,
          totalLoans: 0,
          count: 0,
        });
        return;
      }

      const sortedLoans = [...loans].sort((a, b) => {
        const routeA = (a.route?.name || "").toLocaleLowerCase();
        const routeB = (b.route?.name || "").toLocaleLowerCase();

        const routeCompare = routeA.localeCompare(routeB, "es");
        if (routeCompare !== 0) return routeCompare;

        const dateA = new Date(a.statusDate || a.date || 0).getTime();
        const dateB = new Date(b.statusDate || b.date || 0).getTime();
        return dateB - dateA;
      });

      const mappedRows: CancellationRow[] = sortedLoans.map((loan) => ({
        id: loan.id,
        route: loan.route?.name || "",
        routeId: loan.route?.id || 0,
        idNumber: loan.client.idNumber,
        clientName: loan.client.fullName,
        value: loan.totalValue,
        paidValue: loan.paidValue,
        statusDate: formatDate(loan.statusDate),
        statusDateRaw: loan.statusDate || "",
        date: formatDate(loan.date),
        dateRaw: loan.date || "",
        fullClient: loan.client,
      }));

      setRows(mappedRows);
      showAlert(t('loanReports.cancellations.found', { count: loans.length }), "success")
    } catch (error) {
      console.error("Error fetching cancellations:", error);
      showAlert(t('loanReports.cancellations.loadError'), "error");
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  const handleOpenModal = (client: any) => {
    setSelectedClient({
      cedula: client.idNumber?.toString() || "",
      nombre: client.fullName || "",
      direccion: client.mainAddress || "",
      telefono: client.phone || "",
      oficio: client.job || "",
      calificacion: "",
      celular: client.mobilePhone || "",
    });
    setOpenModal(true);
  };

  const totalValue = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);

  const columns: DataTableColumn<CancellationRow>[] = [
    { id: 'route', header: t('summaryReports.shared.route'), value: (r) => r.route, sortable: true },
    { id: 'idNumber', header: t('common.idNumber'), value: (r) => r.idNumber, sortable: true, hideBelow: 'sm' },
    { id: 'clientName', header: t('loanReports.client'), value: (r) => r.clientName, sortable: true },
    { id: 'value', header: t('loanReports.value'), value: (r) => Number(r.value || 0), render: (r) => formatCurrency(Number(r.value || 0)), align: 'right', sortable: true },
    { id: 'date', header: t('loanReports.loanDate'), value: (r) => r.dateRaw, render: (r) => r.date, csv: (r) => r.date, sortable: true },
    { id: 'statusDate', header: t('loanReports.payoffDate'), value: (r) => r.statusDateRaw, render: (r) => r.statusDate, csv: (r) => r.statusDate, sortable: true },
  ];

  return (
    <ReportPage title={t('loanReports.cancellations.title')} description={t('loanReports.cancellations.description')}>
      <ReportFilters onSubmit={() => handleSubmit({ preventDefault: () => undefined } as SyntheticEvent)} submitDisabled={loading}>
        <DateField
          label={t('loanReports.startDate')}
          value={filter.startDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, startDate: v ?? '' }))}
          max={minYmd(filter.endDate, nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t('loanReports.endDate')}
          value={filter.endDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, endDate: v ?? '' }))}
          min={filter.startDate}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={filter.officeId} onChange={(id) => setFilter((prev) => ({ ...prev, officeId: id }))} />
        <RouteMultiSelect
          routes={officeRoutes}
          value={filter.selectedRouteIds.map(String)}
          onChange={(ids) => setFilter((prev) => ({ ...prev, selectedRouteIds: ids.map(Number) }))}
        />
      </ReportFilters>

      {rows.length > 0 && (
        <ReportStats>
          <StatCard label={t('loanReports.cancellations.count')} value={rows.length} />
          <StatCard label={t('loanReports.cancellations.totalValue')} value={formatCurrency(totalValue)} />
        </ReportStats>
      )}

      <ReportResults>
        <ReportTable<CancellationRow>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading && queried}
          exportFileName={t('loanReports.cancellations.exportFile')}
          ariaLabel={t('loanReports.cancellations.title')}
          emptyState={<ReportEmpty queried={queried} />}
          rowActions={(row) => (
            <RowAction label={t('loanReports.history')} icon={<TimelineOutlined fontSize="small" />} onClick={() => handleOpenModal(row.fullClient)} />
          )}
        />
      </ReportResults>

      <ClientHistoryModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        clientData={selectedClient}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
    </ReportPage>
  );
};

export default Cancellations;
